'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getTempImagesPendingItems,
  getToolTempAudioPendingItems,
  useGlobalModal,
  useRunTempUploadBatch,
} from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type {
  AudioUploadFile,
  FormAudioPrompt,
  FormImagePrompt,
  FormPromptListHelpers,
  FormPromptListItem,
  ImageUploadFile,
  ServiceError,
} from '@/app/types';
import { receiveStudioUpload } from './studio-upload';

function createPromptListHelpers<T extends FormPromptListItem>(
  getPrompts: () => Array<T | null>,
  setPrompts: (prompts: Array<T | null>) => void,
): FormPromptListHelpers<T> {
  return {
    updatePromptItem: (promptId, updater) =>
      setPrompts(getPrompts().map((item) => (!item || item.id !== promptId || !item.prompt ? item : updater(item)))),
    markPromptStatus: (promptId, status) =>
      setPrompts(getPrompts().map((item) => (!item || item.id !== promptId ? item : { ...item, status }))),
  };
}

function createToolImagePromptFromUploadFile(file: ImageUploadFile): FormImagePrompt {
  return { id: file.id, status: file.status === 'done' ? 'done' : 'uploading', prompt: file.file };
}

function createToolAudioPromptFromUploadFile(file: AudioUploadFile): FormAudioPrompt {
  return { id: file.id, status: file.status === 'done' ? 'done' : 'uploading', prompt: file.file };
}

export function useStudioMedia() {
  const t = useTranslations();
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');
  const userInfo = useStore((state) => state.userInfo);
  const [myImage, setMyImage] = useState<FormImagePrompt | null>(null);
  const [logoPrompt, setLogoPrompt] = useState<FormImagePrompt | null>(null);
  const [audioPrompt, setAudioPrompt] = useState<FormAudioPrompt | null>(null);
  const [recordedPrompt, setRecordedPrompt] = useState<FormAudioPrompt | null>(null);
  const imagesRef = useRef<Array<FormImagePrompt | null>>([]);
  const audioRef = useRef<Array<FormAudioPrompt | null>>([]);
  imagesRef.current = [myImage, logoPrompt];
  audioRef.current = [audioPrompt, recordedPrompt];
  const handleAudioUploadError = useCallback(
    (error?: ServiceError) => {
      if (error?.reason !== 'AuditSubmitIllegal' && error?.reason !== 'SensitiveContentDetected') return;
      const auditId = error.metadata?.audit_id;
      openConfirmDialog({
        title: t('popup.dialog.error.checkFailed.title'),
        content: auditId
          ? `${t('sharedComponent.audioUploader.error.checkFailedContent')}\n(ID: ${auditId})`
          : t('sharedComponent.audioUploader.error.checkFailedContent'),
        primaryButtonText: t('popup.dialog.error.confirm'),
      });
    },
    [openConfirmDialog, t],
  );

  useRunTempUploadBatch(
    userInfo,
    'Failed to upload avatar studio images',
    (refs) =>
      getTempImagesPendingItems(
        refs,
        createPromptListHelpers(
          () => imagesRef.current,
          (prompts) => {
            setMyImage((current) => prompts.find((prompt) => prompt?.id === current?.id) ?? current);
            setLogoPrompt((current) => prompts.find((prompt) => prompt?.id === current?.id) ?? current);
          },
        ),
        { imagePrompts: imagesRef.current },
      ),
    [userInfo, myImage, logoPrompt],
  );

  useRunTempUploadBatch(
    userInfo,
    'Failed to upload avatar studio audio',
    (refs) =>
      getToolTempAudioPendingItems(
        refs,
        createPromptListHelpers(
          () => audioRef.current,
          (prompts) => {
            setAudioPrompt((current) => prompts.find((prompt) => prompt?.id === current?.id) ?? current);
            setRecordedPrompt((current) => prompts.find((prompt) => prompt?.id === current?.id) ?? current);
          },
        ),
        { audioPrompts: audioRef.current, onUploadError: handleAudioUploadError },
      ),
    [userInfo, audioPrompt, recordedPrompt, handleAudioUploadError],
  );

  const onAudioUpload = useCallback((file: AudioUploadFile) => {
    setAudioPrompt((current) => receiveStudioUpload(current, file, createToolAudioPromptFromUploadFile));
  }, []);
  const onLogoUpload = useCallback((file: ImageUploadFile) => {
    setLogoPrompt((current) => receiveStudioUpload(current, file, createToolImagePromptFromUploadFile));
  }, []);
  const onImageUpload = useCallback((file: ImageUploadFile) => {
    setMyImage((current) => receiveStudioUpload(current, file, createToolImagePromptFromUploadFile));
  }, []);
  const onRecording = useCallback((blob: Blob, duration: number, name: string) => {
    const id = crypto.randomUUID();
    const extension = blob.type.includes('mp4') ? 'm4a' : 'webm';
    const file = new File([blob], `recording-${id}.${extension}`, { type: blob.type });
    setRecordedPrompt({
      id,
      status: 'done',
      prompt: { src: '', preview: URL.createObjectURL(blob), name, duration, _tempFile: file },
    });
  }, []);

  const recordingPreview = recordedPrompt?.prompt?.preview;
  useEffect(
    () => () => {
      if (recordingPreview?.startsWith('blob:')) URL.revokeObjectURL(recordingPreview);
    },
    [recordingPreview],
  );

  return {
    authenticated: Boolean(userInfo),
    myImage,
    logoPrompt,
    audioPrompt,
    recordedPrompt,
    setMyImage,
    setLogoPrompt,
    setAudioPrompt,
    setRecordedPrompt,
    onImageUpload,
    onLogoUpload,
    onAudioUpload,
    onRecording,
  };
}
